import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../lib/i18n';

/**
 * Choosing which part of a photograph is you.
 *
 * The crop happens here rather than on the server, and not only to save a round trip: the person is
 * the only one who knows where their face is, and a server that guessed would be wrong often enough
 * to be irritating. What leaves this component is a square image at a fixed size, so every avatar in
 * the product is the same shape and weight whatever was uploaded.
 *
 * No cropping library. The whole interaction is a drag and a slider over a canvas, and a dependency
 * for that would be more code than this, loaded on every page that imports it.
 */

/** What the product stores. Twice the largest place it is shown, for high-density screens. */
const OUTPUT = 256;

/** The circle the person drags the image behind. */
const VIEW = 260;

export interface CroppedImage {
  filename: string;
  mimeType: string;
  /** Base64, without the data-URL prefix. */
  content: string;
}

export function AvatarCropper({
  file,
  onCancel,
  onDone,
  busy,
}: {
  file: File;
  onCancel: () => void;
  onDone: (image: CroppedImage) => void;
  busy: boolean;
}): React.JSX.Element {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);

  const dragging = useRef<{ x: number; y: number } | null>(null);

  /**
   * The chosen file, decoded.
   *
   * The object URL is revoked in the cleanup and nowhere else, and a superseded run's callbacks are
   * ignored. Both matter because React's development mode mounts the effect twice: revoking inside
   * `onload` made the first run's image fail, and without the flag that first failure left an error
   * on screen that the successful second run never cleared.
   */
  useEffect(() => {
    let cancelled = false;

    const url = URL.createObjectURL(file);
    const element = new Image();

    element.onload = () => {
      if (cancelled) return;

      setImage(element);
      setError(null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };

    element.onerror = () => {
      if (!cancelled) setError(t('avatar.unreadable'));
    };

    element.src = url;

    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  /**
   * Draws the current crop.
   *
   * The image is scaled so its shorter side covers the circle at zoom 1 — anything less would let
   * somebody produce an avatar with a transparent corner — and the offset is clamped so it can never
   * be dragged away from the frame.
   */
  const draw = useCallback(() => {
    const context = canvas.current?.getContext('2d');

    if (!context || !image) return;

    const cover = Math.max(VIEW / image.width, VIEW / image.height);
    const scale = cover * zoom;

    const width = image.width * scale;
    const height = image.height * scale;

    const maxX = Math.max(0, (width - VIEW) / 2);
    const maxY = Math.max(0, (height - VIEW) / 2);

    const x = Math.min(maxX, Math.max(-maxX, offset.x));
    const y = Math.min(maxY, Math.max(-maxY, offset.y));

    context.clearRect(0, 0, VIEW, VIEW);
    context.drawImage(image, (VIEW - width) / 2 + x, (VIEW - height) / 2 + y, width, height);
  }, [image, zoom, offset]);

  useEffect(() => {
    draw();
  }, [draw]);

  function onPointerDown(event: React.PointerEvent<HTMLCanvasElement>): void {
    dragging.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!dragging.current) return;

    setOffset({ x: event.clientX - dragging.current.x, y: event.clientY - dragging.current.y });
  }

  function onPointerUp(event: React.PointerEvent<HTMLCanvasElement>): void {
    dragging.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  /**
   * Renders the crop at the size the product stores.
   *
   * Always JPEG: a photograph as PNG is several times larger for no visible gain, and an avatar has
   * no transparency to preserve. The white fill matters — a transparent PNG dropped onto a JPEG
   * would otherwise composite against black.
   */
  function finish(): void {
    if (!image) return;

    const output = document.createElement('canvas');

    output.width = OUTPUT;
    output.height = OUTPUT;

    const context = output.getContext('2d');

    if (!context) {
      setError(t('avatar.unreadable'));
      return;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, OUTPUT, OUTPUT);

    const ratio = OUTPUT / VIEW;
    const cover = Math.max(VIEW / image.width, VIEW / image.height);
    const scale = cover * zoom * ratio;

    const width = image.width * scale;
    const height = image.height * scale;

    const maxX = Math.max(0, (width - OUTPUT) / 2);
    const maxY = Math.max(0, (height - OUTPUT) / 2);

    const x = Math.min(maxX, Math.max(-maxX, offset.x * ratio));
    const y = Math.min(maxY, Math.max(-maxY, offset.y * ratio));

    context.drawImage(image, (OUTPUT - width) / 2 + x, (OUTPUT - height) / 2 + y, width, height);

    const dataUrl = output.toDataURL('image/jpeg', 0.88);
    const content = dataUrl.slice(dataUrl.indexOf(',') + 1);

    onDone({ filename: 'avatar.jpg', mimeType: 'image/jpeg', content });
  }

  return (
    <div className="cropper">
      {error ? (
        <p className="alert" role="alert">
          {error}
        </p>
      ) : (
        <>
          <div className="cropper__stage">
            <canvas
              ref={canvas}
              width={VIEW}
              height={VIEW}
              className="cropper__canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
            {/* The circle is an overlay, not a clip: the person needs to see what falls outside it. */}
            <span className="cropper__mask" aria-hidden="true" />
          </div>

          <label className="cropper__zoom">
            <span>{t('avatar.zoom')}</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
          </label>

          <p className="muted small">{t('avatar.dragHint')}</p>

          <div className="cropper__actions">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={onCancel}
            >
              {t('avatar.cancel')}
            </button>

            <button
              type="button"
              className="button button--primary button--compact"
              onClick={finish}
              disabled={!image || busy}
            >
              {busy ? t('avatar.saving') : t('avatar.save')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
